import { cloneElement, useCallback, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Floating, type Placement } from './floating';

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement<Record<string, unknown>>;
  placement?: Placement;
  delayMs?: number;
}

/** Short, non-interactive hint. Shows on hover and keyboard focus, dismissed with Escape. */
export function Tooltip({ content, children, placement = 'top', delayMs = 350 }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const id = useId();

  const show = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), delayMs);
  }, [delayMs]);
  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setOpen(false);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        hide();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, hide]);

  const props = children.props;
  const element = cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;
    },
    'aria-describedby': open ? id : (props['aria-describedby'] as string | undefined),
    onMouseEnter: (e: unknown) => {
      (props.onMouseEnter as ((e: unknown) => void) | undefined)?.(e);
      show();
    },
    onMouseLeave: (e: unknown) => {
      (props.onMouseLeave as ((e: unknown) => void) | undefined)?.(e);
      hide();
    },
    onFocus: (e: unknown) => {
      (props.onFocus as ((e: unknown) => void) | undefined)?.(e);
      show();
    },
    onBlur: (e: unknown) => {
      (props.onBlur as ((e: unknown) => void) | undefined)?.(e);
      hide();
    },
  });

  return (
    <>
      {element}
      {open && (
        <Floating
          anchorRef={anchorRef}
          id={id}
          role="tooltip"
          placement={placement === 'top' || placement === 'bottom' ? placement : 'top'}
          offset={8}
          zIndex={90}
          className="pointer-events-none max-w-xs animate-fade-in rounded-md bg-navy-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-pop ring-1 ring-white/10 dark:bg-navy-100 dark:text-navy-900"
        >
          {content}
        </Floating>
      )}
    </>
  );
}
